import { FeatureFlags } from '../FeatureFlags';
import { LoginErrorCodes, UserErrorCode } from '../helpers/ErrorCodes';
import { Module } from '../Module';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../ServerApi/requests/GeneratePublicKeyRequest';
import {
  IResetPasswordParams,
  ResetPasswordRequest,
} from '../ServerApi/requests/ResetPasswordRequest';
import { IUpdateExpiredPasswordParams, UpdateExpiredPasswordRequest } from '../ServerApi/requests/UpdateExpiredPasswordRequest';
import {
  IValidatePasswordResetTokenParams,
  IValidatePasswordResetTokenResult,
  ValidatePasswordResetTokenRequest,
} from '../ServerApi/requests/ValidatePasswordResetTokenRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { PasswordPolicyScope, PasswordPolicyService } from '../services/PasswordPolicyService';
import { ServerService } from '../services/ServerService';
import { UserLockoutMessageService } from '../services/UserLockoutMessageService';

export interface IPasswordResetScope extends PasswordPolicyScope {
  passwordResetForm: any;
  newPassword: string;
  confirmPassword: string;
  initialized: boolean;
  errorMessages: string[];
  resetPasswordRequest: () => void;
  shouldAskForOldPassword: boolean;
  oldPassword: string;
}

export interface IPasswordResetStateParams extends ng.ui.IStateParamsService {
  token?: string;
  username?: string;
}

enum PasswordResetModes {
  TOKEN, OLD_PASSWORD
}

export class PasswordResetCtrl {

  public static $inject = [
    '$scope',
    '$state',
    '$stateParams',
    '$translate',
    'PasswordPolicyService',
    'UserLockoutMessageService',
    'ServerService',
  ];

  private server: ServerService;
  private state: ng.ui.IStateService;
  private scope: IPasswordResetScope;
  private translate: ng.translate.ITranslateService;
  private respectsPasswordValidation: () => boolean;
  private mode: PasswordResetModes;
  private token: string;
  private username: string;
  private waitingOnPasswordRateLimiting: boolean;
  private userLockoutMessageService: UserLockoutMessageService;

  constructor($scope: IPasswordResetScope,
              $state: ng.ui.IStateService,
              $stateParams: IPasswordResetStateParams,
              $translate: ng.translate.ITranslateService,
              passwordPolicyService: PasswordPolicyService,
              userLockoutMessageService: UserLockoutMessageService,
              server: ServerService) {

    this.server = server;
    this.state = $state;
    this.scope = $scope;
    this.translate = $translate;
    this.token = $stateParams.token;
    this.username = $stateParams.username;
    this.waitingOnPasswordRateLimiting = false;
    this.userLockoutMessageService = userLockoutMessageService;

    this.setMode();

    this.respectsPasswordValidation =
      passwordPolicyService.loadPoliciesIntoScope($scope, () => $scope.newPassword, () => {});

    $scope.resetPasswordRequest = this.sendPasswordResetRequest;
    $scope.initialized = false;
    $scope.shouldAskForOldPassword = this.mode === PasswordResetModes.OLD_PASSWORD;
    this.initialize();
  }

  private setMode = () : void => {
    this.mode = this.token ? PasswordResetModes.TOKEN : PasswordResetModes.OLD_PASSWORD;

    if (this.mode === PasswordResetModes.TOKEN && !this.server.isPasswordResetEnabled()) {
      this.state.go('login.authenticate');
    } else if (this.mode === PasswordResetModes.OLD_PASSWORD && !FeatureFlags.localAuthPasswordExpirationEnabled) {
      this.state.go('login.authenticate');
    }
  }

  private initialize = () : void => {
    if (this.mode === PasswordResetModes.TOKEN) {
      this.validatePasswordResetToken();
    } else {
      this.scope.initialized = true;
    }
  }

  private validatePasswordResetToken = () : void => {
    const requestParams: IValidatePasswordResetTokenParams = {
      token: this.token
    };

    const validateTokenRequest = new ValidatePasswordResetTokenRequest(requestParams);
    this.server.sendRequest(validateTokenRequest)
        .then((validateTokenResult: IValidatePasswordResetTokenResult) => {
              if (validateTokenResult.errors) {
                this.state.go('login.authenticate', {error: validateTokenResult.errors[0].code});
              }
              this.scope.initialized = true;
            }
        );
  }

  private validateFields = () : boolean => {
    if (this.mode === PasswordResetModes.OLD_PASSWORD && !this.scope.oldPassword) {
      this.scope.errorMessages = [this.translate.instant('enterYourOldPassword')];
      angular.element('input[name="oldPassword"]').focus();
      return false;
    }
    if (this.mode === PasswordResetModes.OLD_PASSWORD && this.scope.oldPassword === this.scope.newPassword) {
      this.scope.errorMessages = [this.translate.instant('newPasswordIsSameAsOldPasswordError')];
      angular.element('input[name="newPassword"]').focus();
      return false;
    }
    if (!this.scope.newPassword) {
      this.scope.errorMessages = [this.translate.instant('enterYourPassword')];
      angular.element('input[name="newPassword"]').focus();
      return false;
    }
    if (this.scope.newPassword !== this.scope.confirmPassword) {
      this.scope.errorMessages = [this.translate.instant('passwordsDoNotMatch')];
      angular.element('input[name="confirmPassword"]').focus();
      return false;
    }
    if (!this.respectsPasswordValidation()) {
      return false;
    }
    return true;
  }

  private sendPasswordResetRequest = () : void => {
    if (!this.validateFields()) {
      return;
    }

    this.server.sendRequest(new GeneratePublicKeyRequest())
        .then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
          this.server.sendRequest(<any>this.generateRequest(generatePublicKeyResult))
              .then((resetPasswordResult: IResult) => {
                    if (resetPasswordResult.errors) {
                      this.handleFailure(resetPasswordResult.errors[0].code);
                    } else {
                      this.state.go('login.authenticate', {passwordResetSuccess: true});
                    }
                  }
              );
        });
  }

  private generateRequest = (publicKey: IGeneratePublicKeyResult): ResetPasswordRequest | UpdateExpiredPasswordRequest => {
    if (this.mode === PasswordResetModes.TOKEN) {
      return this.generateTokenRequest(publicKey);
    } else {
      return this.generateUpdatePasswordRequest(publicKey);
    }
  }

  private generateTokenRequest = (publicKey: IGeneratePublicKeyResult) : ResetPasswordRequest => {
    const resetPasswordParams: IResetPasswordParams = {
      resetToken: this.token,
      newEncryptedPassword: rsa.encrypt(this.scope.newPassword, publicKey.key),
      encryptionKeyId: publicKey.keyId
    };
    return new ResetPasswordRequest(resetPasswordParams);
  }

  private generateUpdatePasswordRequest = (publicKey: IGeneratePublicKeyResult) : UpdateExpiredPasswordRequest => {
    const updatedPasswordParams: IUpdateExpiredPasswordParams = {
      username: this.username,
      oldEncryptedPassword: rsa.encrypt(this.scope.oldPassword, publicKey.key),
      newEncryptedPassword: rsa.encrypt(this.scope.newPassword, publicKey.key),
      encryptionKeyId: publicKey.keyId
    };
    return new UpdateExpiredPasswordRequest(updatedPasswordParams);
  }

  private handleFailure = (errorCode: number) : void => {
    if (this.mode === PasswordResetModes.TOKEN) {
      this.state.go('login.authenticate', {error: errorCode});
    } else {
      if (errorCode === LoginErrorCodes.BadCredentials || errorCode === UserErrorCode.UserInvalidPassword) {
        this.scope.errorMessages = [this.translate.instant('errorUserOldPasswordIncorrect')];
      } else if (errorCode === UserErrorCode.LoginAttemptRejected) {
        this.showRateLimitingError();
      } else if (errorCode === UserErrorCode.AccountIsBlocked) {
        this.scope.errorMessages = [this.translate.instant('errorAccountIsBlocked')];
      } else if (errorCode === UserErrorCode.PasswordNotUpdated) {
        this.scope.errorMessages = [this.translate.instant('errorPasswordNotUpdated')];
      } else {
        this.scope.errorMessages = [this.translate.instant('unexpectedError')];
      }
    }
  }

  private showRateLimitingError(): void {
    if (this.waitingOnPasswordRateLimiting) {
      return;
    }
    this.waitingOnPasswordRateLimiting = true;
    const originalMessages = this.scope.errorMessages;

    this.userLockoutMessageService.showMessageForUser(this.username, (message: string) => {
      // New message handler.
      const newMessages = [...originalMessages];
      newMessages.push(message);
      this.scope.errorMessages = newMessages;
    }, () => {
      // On complete.
      this.waitingOnPasswordRateLimiting = false;
      this.scope.errorMessages = originalMessages;
    });
  }
}

Module.instance().controller('PasswordResetCtrl', PasswordResetCtrl);
